import { useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { useCanvasStores } from '../stores/CanvasContext';
import { useCanvasSidebarResize } from '../hooks/useCanvasSidebarResize';
import { CanvasLayerPanelContent } from './layerpanel/CanvasLayerPanel';
import { CanvasSpritePanel } from './CanvasSpritePanel';
import { AdrSidebarPanel } from './AdrSidebarPanel';
import { ResourceSidebarPanel } from './ResourceSidebarPanel';
import { CommandHistoryPanel } from './CommandHistoryPanel';

export const CanvasSidebar = memo(function CanvasSidebar() {
  const { t } = useTranslation();
  const { uiStore } = useCanvasStores();
  const sidebarWidth = useSelector(uiStore, s => s.context.sidebarWidth);
  const isSidebarCollapsed = useSelector(uiStore, s => s.context.isSidebarCollapsed);
  const isSidebarResizing = useSelector(uiStore, s => s.context.isSidebarResizing);
  const [openSection, setOpenSection] = useState<'layers' | 'sprites' | 'adrs' | 'resources' | 'history' | null>('layers');

  const { handleResizeStart, handleDoubleClick, toggleCollapse } = useCanvasSidebarResize(uiStore);

  return (
    <div
      style={{
        width: sidebarWidth,
        flexShrink: 0,
        backgroundColor: '#fff',
        borderRight: '1px solid #e2e8f0',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {isSidebarCollapsed ? (
        <>
          {/* Collapsed: expand button */}
          <div
            style={{
              height: 32,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              cursor: 'pointer',
            }}
            onClick={toggleCollapse}
            title={t('canvasLayer.expandSidebar')}
          >
            <ChevronRight size={12} style={{ color: '#64748b' }} />
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={toggleCollapse}
          >
            <span
              style={{
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
                fontSize: 11,
                fontWeight: 500,
                color: '#64748b',
                letterSpacing: '0.05em',
              }}
            >
              Sidebar
            </span>
          </div>
        </>
      ) : (
        <>
          {/* Layers accordion section */}
          <AccordionHeader label="Layers" isOpen={openSection === 'layers'} onToggle={() => setOpenSection(s => s === 'layers' ? null : 'layers')} />
          {openSection === 'layers' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <CanvasLayerPanelContent />
            </div>
          )}

          {/* Sprites accordion section */}
          <AccordionHeader label="Sprites" isOpen={openSection === 'sprites'} onToggle={() => setOpenSection(s => s === 'sprites' ? null : 'sprites')} />
          {openSection === 'sprites' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <CanvasSpritePanel />
            </div>
          )}

          {/* ADRs accordion section */}
          <AccordionHeader label="ADRs" isOpen={openSection === 'adrs'} onToggle={() => setOpenSection(s => s === 'adrs' ? null : 'adrs')} />
          {openSection === 'adrs' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <AdrSidebarPanel />
            </div>
          )}

          {/* Resources accordion section */}
          <AccordionHeader label="Resources" isOpen={openSection === 'resources'} onToggle={() => setOpenSection(s => s === 'resources' ? null : 'resources')} />
          {openSection === 'resources' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <ResourceSidebarPanel />
            </div>
          )}

          {/* History accordion section */}
          <AccordionHeader label="History" isOpen={openSection === 'history'} onToggle={() => setOpenSection(s => s === 'history' ? null : 'history')} />
          {openSection === 'history' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <CommandHistoryPanel />
            </div>
          )}
        </>
      )}

      {/* Resize Handle */}
      <div
        onMouseDown={handleResizeStart}
        onDoubleClick={handleDoubleClick}
        style={{
          position: 'absolute',
          top: 0,
          right: -2,
          width: 4,
          height: '100%',
          cursor: 'col-resize',
          zIndex: 31,
          backgroundColor: isSidebarResizing ? 'rgba(59, 130, 246, 0.3)' : 'transparent',
        }}
        onMouseEnter={(e) => {
          if (!isSidebarResizing) {
            e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSidebarResizing) {
            e.currentTarget.style.backgroundColor = 'transparent';
          }
        }}
      />
    </div>
  );
});

function AccordionHeader({ label, isOpen, onToggle }: { label: string; isOpen: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      style={{
        height: 28,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        paddingLeft: 8,
        paddingRight: 8,
        fontSize: 10,
        fontWeight: 500,
        color: '#374151',
        background: '#f8fafc',
        border: 'none',
        borderBottom: '1px solid #e2e8f0',
        cursor: 'pointer',
        width: '100%',
        textAlign: 'left',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; }}
      onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; }}
    >
      <ChevronDown
        size={10}
        style={{
          color: '#94a3b8',
          transition: 'transform 150ms',
          transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
          flexShrink: 0,
        }}
      />
      {label}
    </button>
  );
}
